import { X, Target } from 'lucide-react';

import { cn } from '@/lib/utils';

interface LotteryRulesDrawerProps {
  open: boolean;
  onClose: () => void;
}

export function LotteryRulesDrawer({ open, onClose }: LotteryRulesDrawerProps) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 lg:inset-y-0 lg:right-0 lg:left-auto lg:w-96">
      {/* Backdrop (mobile only) */}
      <div className="lg:hidden absolute inset-0 bg-black/95 backdrop-blur-sm" onClick={onClose} />

      {/* Drawer */}
      <div
        className={cn(
          'absolute inset-x-0 bottom-0 lg:inset-y-0 lg:right-0',
          'border-t lg:border-t-0 lg:border-l',
          'lg:w-96 rounded-t-lg lg:rounded-t-none shadow-xl',
          'max-h-[85vh] lg:max-h-full overflow-hidden',
          'transition-transform duration-300 ease-out',
          'translate-y-0',
          'flex flex-col'
        )}
        style={{
          backgroundColor: '#343434',
          borderColor: '#9D4EDD',
          boxShadow: '0 0 20px rgba(157, 78, 221, 0.3)',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4" style={{ borderBottom: '1px solid rgba(157, 78, 221, 0.3)' }}>
          <div className="flex items-center space-x-2">
            <Target className="h-5 w-5" style={{ color: '#9D4EDD' }} />
            <h2 className="font-semibold text-lg" style={{ fontFamily: 'Hyperspace Race Capsule, cursive', color: 'white' }}>LOTTERY RULES</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-md transition-colors"
            style={{ color: 'white' }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            aria-label="Close rules"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Key Stats Banner */}
        <div className="px-4 py-3" style={{ backgroundColor: 'rgba(157, 78, 221, 0.1)', borderBottom: '1px solid rgba(157, 78, 221, 0.2)' }}>
          <div className="grid grid-cols-2 gap-3 text-center">
            <div>
              <div className="text-xs" style={{ fontFamily: 'Outfit, sans-serif', color: 'rgba(255, 255, 255, 0.7)' }}>
                Season Duration
              </div>
              <div className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: '#9D4EDD' }}>
                15 Days
              </div>
              <div className="text-xs" style={{ fontFamily: 'Outfit, sans-serif', color: 'rgba(255, 255, 255, 0.5)' }}>
                2 Phases
              </div>
            </div>
            <div>
              <div className="text-xs" style={{ fontFamily: 'Outfit, sans-serif', color: 'rgba(255, 255, 255, 0.7)' }}>
                Min. Investment
              </div>
              <div className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: '#22C55E' }}>
                100 Points
              </div>
              <div className="text-xs" style={{ fontFamily: 'Outfit, sans-serif', color: 'rgba(255, 255, 255, 0.5)' }}>
                Multiples of 100
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto min-h-0 p-4">
          <div className="space-y-4">
            {/* Phase Overview */}
            <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(157, 78, 221, 0.1)', border: '2px solid rgba(157, 78, 221, 0.3)' }}>
              <h4 className="font-semibold mb-3 flex items-center space-x-2" style={{ fontFamily: 'Hyperspace Race Capsule, cursive', color: 'white' }}>
                <span>🔄</span>
                <span>SEASON OVERVIEW</span>
              </h4>
              <div className="text-sm space-y-2" style={{ fontFamily: 'Outfit, sans-serif', color: 'rgba(255, 255, 255, 0.9)', lineHeight: '1.6' }}>
                <p>The Points Lottery operates on a seasonal basis with each season lasting <strong style={{ color: '#9D4EDD' }}>15 days</strong>, divided into two alternating phases:</p>
              </div>
            </div>

            {/* Preparation Phase */}
            <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(80, 228, 249, 0.1)', border: '2px solid rgba(80, 228, 249, 0.3)' }}>
              <h4 className="font-semibold mb-3 flex items-center space-x-2" style={{ fontFamily: 'Hyperspace Race Capsule, cursive', color: 'white' }}>
                <span>⏳</span>
                <span>PREPARATION PHASE</span>
              </h4>
              <div className="text-sm space-y-2" style={{ fontFamily: 'Outfit, sans-serif', color: 'rgba(255, 255, 255, 0.9)', lineHeight: '1.6' }}>
                <p className="mb-2"><strong style={{ color: '#50E5F8' }}>Duration: 3 days</strong></p>
                <div className="space-y-2">
                  <p><strong style={{ color: '#50E5F8' }}>1.</strong> Users can view their winning status and the corresponding winner list from the previous season. If they have won, they can claim their rewards.</p>
                  <p><strong style={{ color: '#50E5F8' }}>2.</strong> During this phase, users cannot participate in the lottery but can still engage in Junk activities and open chests to earn points.</p>
                  <p><strong style={{ color: '#50E5F8' }}>3.</strong> If winners do not claim their rewards during this phase, the system will automatically distribute the rewards to their Junk Fun accounts at the start of the next phase.</p>
                </div>
              </div>
            </div>

            {/* Active Phase */}
            <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)', border: '2px solid rgba(34, 197, 94, 0.3)' }}>
              <h4 className="font-semibold mb-3 flex items-center space-x-2" style={{ fontFamily: 'Hyperspace Race Capsule, cursive', color: 'white' }}>
                <span>⚡</span>
                <span>ACTIVE PHASE</span>
              </h4>
              <div className="text-sm space-y-2" style={{ fontFamily: 'Outfit, sans-serif', color: 'rgba(255, 255, 255, 0.9)', lineHeight: '1.6' }}>
                <p className="mb-2"><strong style={{ color: '#22C55E' }}>Duration: 12 days</strong></p>
                <div className="space-y-2">
                  <p><strong style={{ color: '#22C55E' }}>1.</strong> Users can invest points to participate in the final prize pool distribution.</p>
                  <p><strong style={{ color: '#22C55E' }}>2.</strong> The more points a user invests, the higher their chances of winning the grand prize.</p>
                  <p><strong style={{ color: '#22C55E' }}>3.</strong> Each participation requires a minimum investment of <strong>100 points</strong>, and users may invest additional points in multiples of 100.</p>
                  <p><strong style={{ color: '#22C55E' }}>4.</strong> The total points invested by all users will determine the final rewards. Higher total points invested will unlock higher-tier prize pools.</p>
                  <p><strong style={{ color: '#22C55E' }}>5.</strong> At the end of this phase, the system will conduct the lottery, randomly selecting all winners and distributing the corresponding rewards.</p>
                </div>
              </div>
            </div>

            {/* How to Participate */}
            <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(157, 78, 221, 0.1)' }}>
              <h4 className="font-semibold mb-3 flex items-center space-x-2" style={{ fontFamily: 'Hyperspace Race Capsule, cursive', color: 'white' }}>
                <span>💡</span>
                <span>HOW TO PARTICIPATE</span>
              </h4>
              <div className="text-sm space-y-2" style={{ fontFamily: 'Outfit, sans-serif', color: 'rgba(255, 255, 255, 0.9)' }}>
                <p style={{ fontFamily: 'Outfit, sans-serif' }}>• Navigate to the Points page during the Active Phase</p>
                <p style={{ fontFamily: 'Outfit, sans-serif' }}>• Select amount of points to invest (min. 100, multiples of 100)</p>
                <p style={{ fontFamily: 'Outfit, sans-serif' }}>• Click "Invest Points" to confirm your entry</p>
                <p style={{ fontFamily: 'Outfit, sans-serif' }}>• More points = higher chances of winning</p>
                <p style={{ fontFamily: 'Outfit, sans-serif' }}>• Track your investment and odds in real-time</p>
                <p style={{ fontFamily: 'Outfit, sans-serif' }}>• Winners announced at end of Active Phase</p>
              </div>
            </div>

            {/* Prize Pool Unlocking */}
            <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(254, 187, 1, 0.1)', border: '1px solid rgba(254, 187, 1, 0.3)' }}>
              <h4 className="font-semibold mb-3 flex items-center space-x-2" style={{ fontFamily: 'Hyperspace Race Capsule, cursive', color: 'white' }}>
                <span>🎁</span>
                <span>PRIZE POOL TIERS</span>
              </h4>
              <div className="text-sm space-y-2" style={{ fontFamily: 'Outfit, sans-serif', color: 'rgba(255, 255, 255, 0.9)' }}>
                <p className="mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Prize pools unlock progressively based on total community investment:</p>
                <div className="rounded-lg p-3 space-y-2" style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}>
                  <div className="flex justify-between">
                    <span>More participation</span>
                    <span className="font-bold" style={{ color: '#FEBB01' }}>→</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Higher total investment</span>
                    <span className="font-bold" style={{ color: '#FEBB01' }}>→</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Bigger prize pools unlocked!</span>
                    <span className="font-bold" style={{ color: '#FEBB01' }}>🏆</span>
                  </div>
                </div>
                <p className="text-xs mt-2" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Check the main lottery page to see current tier progress and unlock thresholds.
                </p>
              </div>
            </div>

            {/* Important Notes */}
            <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
              <h4 className="font-semibold mb-3 flex items-center space-x-2" style={{ fontFamily: 'Hyperspace Race Capsule, cursive', color: '#EF4444' }}>
                <span>⚠️</span>
                <span>IMPORTANT NOTES</span>
              </h4>
              <div className="text-sm space-y-2" style={{ fontFamily: 'Outfit, sans-serif', color: 'rgba(255, 255, 255, 0.9)' }}>
                <p style={{ fontFamily: 'Outfit, sans-serif' }}>• Points invested cannot be withdrawn once submitted</p>
                <p style={{ fontFamily: 'Outfit, sans-serif' }}>• Claiming rewards is available during Preparation Phase</p>
                <p style={{ fontFamily: 'Outfit, sans-serif' }}>• Unclaimed rewards auto-distribute (no loss!)</p>
                <p style={{ fontFamily: 'Outfit, sans-serif' }}>• Each season is independent - new chance every 15 days</p>
                <p style={{ fontFamily: 'Outfit, sans-serif' }}>• Winners selected randomly based on investment weight</p>
                <p style={{ fontFamily: 'Outfit, sans-serif' }}>• Keep earning points between seasons for next round</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

